import { useMemo } from "react";
import { useDocumentDisplayCurrency } from "@/ProposalBuilder/hooks/useDocumentDisplayCurrency";
import { getCurrencySettings } from "@/Utils/Currency";

export interface CatalogPriceConversion {
	/** True when a non-primary document context is in scope and conversion should be applied. */
	active: boolean;
	/** Currency code to show in the input addon and labels. */
	addonCurrency: string;
	/**
	 * Convert a catalog (primary-currency) value to the value shown to the user.
	 * No-op outside a non-primary-doc context.
	 */
	toDisplay: (catalogValue: number | null | undefined) => number;
	/**
	 * Convert a user-entered (display-currency) value back to the primary-currency value
	 * that gets persisted to the catalog.
	 * No-op outside a non-primary-doc context.
	 */
	fromDisplay: (displayValue: number | null | undefined) => number;
	/** The active rate used for the conversion (1 when inactive). */
	rate: number;
}

export interface CatalogPricingTriple {
	cost_price: number;
	sell_price: number;
	setup_price: number;
}

/**
 * Convert the raw catalog/primary-currency `cost_price`, `sell_price`, and
 * `setup_price` of a pricing plan into display-currency values for the form
 * to render. No-op when `priceConversion.active` is false.
 */
export const toDisplayPricingTriple = (
	pricing:
		| {
				cost_price?: number | null;
				sell_price?: number | null;
				setup_price?: number | null;
		  }
		| null
		| undefined,
	priceConversion: CatalogPriceConversion
): CatalogPricingTriple => ({
	cost_price: priceConversion.toDisplay(pricing?.cost_price ?? 0),
	sell_price: priceConversion.toDisplay(pricing?.sell_price ?? 0),
	setup_price: priceConversion.toDisplay(pricing?.setup_price ?? 0)
});

/**
 * Inverse of `toDisplayPricingTriple` — convert the form's display-currency
 * cost / sell / setup back to the catalog/primary-currency values that get
 * persisted on the pricing plan. AntD-v3 form names: `selected_cost_price`,
 * `sell_price`, `selected_setup_price`. No-op when `priceConversion.active`
 * is false.
 */
export const fromDisplayPricingTriple = (
	formValues:
		| {
				selected_cost_price?: number | null;
				sell_price?: number | null;
				selected_setup_price?: number | null;
		  }
		| null
		| undefined,
	priceConversion: CatalogPriceConversion
): CatalogPricingTriple => ({
	cost_price: priceConversion.fromDisplay(
		formValues?.selected_cost_price ?? 0
	),
	sell_price: priceConversion.fromDisplay(formValues?.sell_price ?? 0),
	setup_price: priceConversion.fromDisplay(
		formValues?.selected_setup_price ?? 0
	)
});

export const useCatalogPriceConversion = (): CatalogPriceConversion => {
	const { isLegacy, quoteCurrency, companyCurrency, effectiveRate } =
		useDocumentDisplayCurrency();

	return useMemo(() => {
		const fallbackCompanyCurrency =
			companyCurrency || getCurrencySettings().currency;

		const active =
			!isLegacy &&
			!!quoteCurrency &&
			!!companyCurrency &&
			quoteCurrency !== companyCurrency &&
			!!effectiveRate &&
			effectiveRate > 0;

		const rate = active ? effectiveRate : 1;
		const addonCurrency = active
			? (quoteCurrency as string)
			: fallbackCompanyCurrency;

		return {
			active,
			addonCurrency,
			rate,
			toDisplay: catalogValue =>
				active ? (catalogValue ?? 0) * rate : catalogValue ?? 0,
			fromDisplay: displayValue =>
				active ? (displayValue ?? 0) / rate : displayValue ?? 0
		};
	}, [isLegacy, quoteCurrency, companyCurrency, effectiveRate]);
};
